/**
 * `/quote/catalog` — Catalog management (M-QUOTE.3.b).
 *
 * Three-tab layout for the three catalog tables shipped in #76:
 *   - Items (services this workspace sells)
 *   - Pricing rules (surcharges / multipliers / minimums applied at draft time)
 *   - Service areas (geographic regions the workspace covers)
 *
 * The Items tab keeps the inline edit/delete UI from #77. The two new tabs
 * are list + add-form + delete only — neither has an update endpoint
 * (mirrors the sequence editor scope: create-only until a PATCH endpoint
 * lands, since these don't have downstream enrollment-style coupling
 * that would break on delete-then-recreate but the simpler API surface
 * has been good enough).
 *
 * As of 2026-05-22 nothing in the Quote draft pipeline reads pricing rules
 * or service areas — these CRUD surfaces let admins pre-populate the data
 * so the pipeline can light up immediately when wiring lands.
 */

import type {
  QuoteCatalogItemResponse,
  QuotePricingRuleResponse,
  QuoteServiceAreaResponse,
} from "@simpliwork/api-schema";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useCreateQuoteCatalogItem,
  useCreateQuotePricingRule,
  useCreateQuoteServiceArea,
  useDeleteQuoteCatalogItem,
  useDeleteQuotePricingRule,
  useDeleteQuoteServiceArea,
  useQuoteCatalog,
  useQuotePricingRules,
  useQuoteServiceAreas,
  useUpdateQuoteCatalogItem,
} from "@/hooks/use-quote";

type TabId = "items" | "pricing-rules" | "service-areas";

export function QuoteCatalogPage() {
  const [tab, setTab] = useState<TabId>("items");

  return (
    <div className="flex flex-col gap-6">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-1">Quote</p>
        <h1 className="text-2xl font-extrabold tracking-tight text-gray-900">Service catalog</h1>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The items, pricing rules, and service areas that drive how AI drafts quotes.
        </p>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabId)}>
        <TabsList>
          <TabsTrigger value="items" data-testid="quote-catalog-tab-items">
            Items
          </TabsTrigger>
          <TabsTrigger value="pricing-rules" data-testid="quote-catalog-tab-pricing-rules">
            Pricing rules
          </TabsTrigger>
          <TabsTrigger value="service-areas" data-testid="quote-catalog-tab-service-areas">
            Service areas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="items">
          <ItemsPanel />
        </TabsContent>
        <TabsContent value="pricing-rules">
          <PricingRulesPanel />
        </TabsContent>
        <TabsContent value="service-areas">
          <ServiceAreasPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── Items tab ─────────────────────────────────────────────────────────────

function ItemsPanel() {
  const list = useQuoteCatalog();
  const createMut = useCreateQuoteCatalogItem();
  const deleteMut = useDeleteQuoteCatalogItem();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    createMut.mutate(
      {
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(priceMin.trim() ? { base_price_min: priceMin.trim() } : {}),
        ...(priceMax.trim() ? { base_price_max: priceMax.trim() } : {}),
      },
      {
        onSuccess: () => {
          setName("");
          setDescription("");
          setPriceMin("");
          setPriceMax("");
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Add catalog item</CardTitle>
          <CardDescription>
            Name is required; price range is optional but recommended.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={submit}
            className="grid grid-cols-1 gap-3 md:grid-cols-2"
            data-testid="quote-catalog-items-add-form"
          >
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Service name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="quote-catalog-items-name-input"
            />
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Min price (e.g. 99.00)"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              pattern="\d+(\.\d{1,2})?"
            />
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Max price (e.g. 499.00)"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              pattern="\d+(\.\d{1,2})?"
            />
            <div className="md:col-span-2">
              <Button
                type="submit"
                disabled={createMut.isPending}
                data-testid="quote-catalog-items-add-cta"
              >
                {createMut.isPending ? "Adding…" : "Add item"}
              </Button>
              {createMut.error && (
                <span className="ml-3 text-sm text-destructive">{createMut.error.message}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {list.isLoading && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}
      {list.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive" data-testid="quote-catalog-items-error">
              Couldn't load catalog: {list.error.message}
            </p>
          </CardContent>
        </Card>
      )}
      {list.data && list.data.items.length === 0 && !list.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>No items yet</CardTitle>
            <CardDescription>
              Add your first service above. Quote AI references this catalog when drafting
              responses, so even a rough list helps.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {list.data && list.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-3" data-testid="quote-catalog-items-list">
          {list.data.items.map((item) => (
            <CatalogItemRow key={item.id} item={item} onDelete={() => deleteMut.mutate(item.id)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function CatalogItemRow({
  item,
  onDelete,
}: {
  item: QuoteCatalogItemResponse;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description ?? "");
  const [priceMin, setPriceMin] = useState(item.base_price_min ?? "");
  const [priceMax, setPriceMax] = useState(item.base_price_max ?? "");
  const updateMut = useUpdateQuoteCatalogItem();

  function save() {
    updateMut.mutate(
      {
        id: item.id,
        body: {
          name: name.trim(),
          description: description.trim() || undefined,
          base_price_min: priceMin.trim() || undefined,
          base_price_max: priceMax.trim() || undefined,
        },
      },
      { onSuccess: () => setEditing(false) },
    );
  }

  if (!editing) {
    return (
      <li>
        <Card>
          <CardContent className="flex items-start justify-between gap-3 pt-6">
            <div>
              <div className="font-medium">{item.name}</div>
              {item.description && (
                <div className="text-sm text-muted-foreground">{item.description}</div>
              )}
              <div className="mt-1 text-xs text-muted-foreground">
                {item.base_price_min && item.base_price_max
                  ? `$${item.base_price_min} – $${item.base_price_max}`
                  : item.base_price_min
                    ? `From $${item.base_price_min}`
                    : "No price set"}
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button variant="outline" size="sm" onClick={onDelete}>
                Delete
              </Button>
            </div>
          </CardContent>
        </Card>
      </li>
    );
  }

  return (
    <li>
      <Card>
        <CardContent className="grid grid-cols-1 gap-2 pt-6 md:grid-cols-2">
          <input
            className="rounded border border-input bg-background px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <input
            className="rounded border border-input bg-background px-3 py-2 text-sm"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <input
            className="rounded border border-input bg-background px-3 py-2 text-sm"
            placeholder="Min price"
            value={priceMin}
            onChange={(e) => setPriceMin(e.target.value)}
          />
          <input
            className="rounded border border-input bg-background px-3 py-2 text-sm"
            placeholder="Max price"
            value={priceMax}
            onChange={(e) => setPriceMax(e.target.value)}
          />
          <div className="md:col-span-2 flex gap-2">
            <Button size="sm" onClick={save} disabled={updateMut.isPending || !name.trim()}>
              {updateMut.isPending ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            {updateMut.error && (
              <span className="text-xs text-destructive self-center">
                {updateMut.error.message}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </li>
  );
}

// ── Pricing rules tab ─────────────────────────────────────────────────────

function PricingRulesPanel() {
  const list = useQuotePricingRules();
  const createMut = useCreateQuotePricingRule();
  const deleteMut = useDeleteQuotePricingRule();

  const [name, setName] = useState("");
  const [ruleType, setRuleType] = useState("fixed_surcharge");
  const [priority, setPriority] = useState(0);
  const [configJson, setConfigJson] = useState('{"amount": "25.00"}');
  const [configError, setConfigError] = useState<string | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    let parsedConfig: Record<string, unknown> = {};
    try {
      parsedConfig = configJson.trim() === "" ? {} : JSON.parse(configJson);
      setConfigError(null);
    } catch (err) {
      setConfigError((err as Error).message);
      return;
    }
    createMut.mutate(
      {
        name: name.trim(),
        rule_type: ruleType.trim(),
        config: parsedConfig,
        priority,
      },
      {
        onSuccess: () => {
          setName("");
          setConfigJson('{"amount": "25.00"}');
          setPriority(0);
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Add pricing rule</CardTitle>
          <CardDescription>
            Adjusts drafted quote prices. Higher priority wins when multiple rules match. The Quote
            draft pipeline doesn't read these yet — pre-populate so they're ready when wiring lands.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={submit}
            className="grid grid-cols-1 gap-3 md:grid-cols-2"
            data-testid="quote-catalog-rules-add-form"
          >
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Rule name (e.g. After-hours surcharge)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="quote-catalog-rules-name-input"
            />
            <select
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              value={ruleType}
              onChange={(e) => setRuleType(e.target.value)}
              data-testid="quote-catalog-rules-type-select"
            >
              <option value="fixed_surcharge">fixed_surcharge</option>
              <option value="percentage_uplift">percentage_uplift</option>
              <option value="minimum_charge">minimum_charge</option>
              <option value="discount">discount</option>
            </select>
            <input
              type="number"
              min={0}
              max={1000}
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Priority (default 0)"
              value={priority}
              onChange={(e) => setPriority(Number.parseInt(e.target.value || "0", 10))}
              data-testid="quote-catalog-rules-priority-input"
            />
            <textarea
              className="rounded border border-input bg-background px-3 py-2 text-sm font-mono md:col-span-2 min-h-[80px]"
              placeholder='Config (JSON). Examples: {"amount":"25.00"}, {"pct":15}'
              value={configJson}
              onChange={(e) => setConfigJson(e.target.value)}
              data-testid="quote-catalog-rules-config-textarea"
            />
            <div className="md:col-span-2 flex items-center gap-3">
              <Button
                type="submit"
                disabled={createMut.isPending}
                data-testid="quote-catalog-rules-add-cta"
              >
                {createMut.isPending ? "Adding…" : "Add rule"}
              </Button>
              {configError && (
                <span className="text-sm text-destructive" data-testid="quote-catalog-rules-config-error">
                  Invalid JSON: {configError}
                </span>
              )}
              {createMut.error && (
                <span className="text-sm text-destructive">{createMut.error.message}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {list.isLoading && <Skeleton className="h-16 w-full" />}
      {list.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive" data-testid="quote-catalog-rules-list-error">
              Couldn't load pricing rules: {list.error.message}
            </p>
          </CardContent>
        </Card>
      )}
      {list.data && list.data.items.length === 0 && !list.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>No pricing rules yet</CardTitle>
            <CardDescription>
              Add your first rule above. Even one fixed surcharge or percentage uplift helps the
              AI quote a more realistic range.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {list.data && list.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-3" data-testid="quote-catalog-rules-list">
          {list.data.items.map((rule) => (
            <PricingRuleRow
              key={rule.id}
              rule={rule}
              onDelete={() => deleteMut.mutate(rule.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function PricingRuleRow({
  rule,
  onDelete,
}: {
  rule: QuotePricingRuleResponse;
  onDelete: () => void;
}) {
  return (
    <li>
      <Card>
        <CardContent className="flex items-start justify-between gap-3 pt-6">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{rule.name}</span>
              <Badge variant="outline">{rule.rule_type}</Badge>
              <Badge variant={rule.is_active ? "default" : "outline"}>
                {rule.is_active ? "active" : "inactive"}
              </Badge>
              <span className="text-xs text-muted-foreground">priority {rule.priority}</span>
            </div>
            <pre className="mt-2 overflow-x-auto rounded bg-muted/40 p-2 text-xs">
              {JSON.stringify(rule.config, null, 2)}
            </pre>
          </div>
          <Button variant="outline" size="sm" onClick={onDelete}>
            Delete
          </Button>
        </CardContent>
      </Card>
    </li>
  );
}

// ── Service areas tab ─────────────────────────────────────────────────────

function ServiceAreasPanel() {
  const list = useQuoteServiceAreas();
  const createMut = useCreateQuoteServiceArea();
  const deleteMut = useDeleteQuoteServiceArea();

  const [name, setName] = useState("");
  const [zipCsv, setZipCsv] = useState("");
  const [radius, setRadius] = useState("");
  const [centerLat, setCenterLat] = useState("");
  const [centerLng, setCenterLng] = useState("");

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const zips = zipCsv
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    createMut.mutate(
      {
        name: name.trim(),
        ...(zips.length > 0 ? { zip_codes: zips } : {}),
        ...(radius.trim() ? { radius_miles: radius.trim() } : {}),
        ...(centerLat.trim() ? { center_lat: centerLat.trim() } : {}),
        ...(centerLng.trim() ? { center_lng: centerLng.trim() } : {}),
      },
      {
        onSuccess: () => {
          setName("");
          setZipCsv("");
          setRadius("");
          setCenterLat("");
          setCenterLng("");
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Add service area</CardTitle>
          <CardDescription>
            Define coverage by ZIP list, a radius around a lat/lng, or both. Quote pipeline can
            filter leads to in-area when wiring lands.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={submit}
            className="grid grid-cols-1 gap-3 md:grid-cols-2"
            data-testid="quote-catalog-areas-add-form"
          >
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm md:col-span-2"
              placeholder="Area name (e.g. Greater Boston)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="quote-catalog-areas-name-input"
            />
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm md:col-span-2"
              placeholder="ZIP codes, comma-separated (e.g. 02446, 02115, 02134)"
              value={zipCsv}
              onChange={(e) => setZipCsv(e.target.value)}
              data-testid="quote-catalog-areas-zip-input"
            />
            <input
              className="rounded border border-input bg-background px-3 py-2 text-sm"
              placeholder="Radius (miles, e.g. 15.00)"
              value={radius}
              onChange={(e) => setRadius(e.target.value)}
              pattern="\d+(\.\d{1,2})?"
              data-testid="quote-catalog-areas-radius-input"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                className="rounded border border-input bg-background px-3 py-2 text-sm"
                placeholder="Center lat (e.g. 42.3601)"
                value={centerLat}
                onChange={(e) => setCenterLat(e.target.value)}
                pattern="-?\d{1,3}(\.\d+)?"
                data-testid="quote-catalog-areas-lat-input"
              />
              <input
                className="rounded border border-input bg-background px-3 py-2 text-sm"
                placeholder="Center lng (e.g. -71.0589)"
                value={centerLng}
                onChange={(e) => setCenterLng(e.target.value)}
                pattern="-?\d{1,3}(\.\d+)?"
                data-testid="quote-catalog-areas-lng-input"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                type="submit"
                disabled={createMut.isPending}
                data-testid="quote-catalog-areas-add-cta"
              >
                {createMut.isPending ? "Adding…" : "Add area"}
              </Button>
              {createMut.error && (
                <span className="ml-3 text-sm text-destructive">{createMut.error.message}</span>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {list.isLoading && <Skeleton className="h-16 w-full" />}
      {list.error && (
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-destructive" data-testid="quote-catalog-areas-list-error">
              Couldn't load service areas: {list.error.message}
            </p>
          </CardContent>
        </Card>
      )}
      {list.data && list.data.items.length === 0 && !list.isLoading && (
        <Card>
          <CardHeader>
            <CardTitle>No service areas yet</CardTitle>
            <CardDescription>
              Add at least one so leads outside your coverage can be flagged when the Quote pipeline
              starts respecting these.
            </CardDescription>
          </CardHeader>
        </Card>
      )}
      {list.data && list.data.items.length > 0 && (
        <ul className="grid grid-cols-1 gap-3" data-testid="quote-catalog-areas-list">
          {list.data.items.map((area) => (
            <ServiceAreaRow
              key={area.id}
              area={area}
              onDelete={() => deleteMut.mutate(area.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function ServiceAreaRow({
  area,
  onDelete,
}: {
  area: QuoteServiceAreaResponse;
  onDelete: () => void;
}) {
  const zips = area.zip_codes ?? [];
  const hasCircle = area.radius_miles && area.center_lat && area.center_lng;
  return (
    <li>
      <Card>
        <CardContent className="flex items-start justify-between gap-3 pt-6">
          <div className="min-w-0 flex-1">
            <div className="font-medium">{area.name}</div>
            {zips.length > 0 && (
              <div className="mt-1 text-xs text-muted-foreground">
                ZIPs ({zips.length}): {zips.slice(0, 6).join(", ")}
                {zips.length > 6 ? `, +${zips.length - 6} more` : ""}
              </div>
            )}
            {hasCircle && (
              <div className="mt-1 text-xs text-muted-foreground">
                {area.radius_miles} mi around ({area.center_lat}, {area.center_lng})
              </div>
            )}
            {zips.length === 0 && !hasCircle && (
              <div className="mt-1 text-xs text-muted-foreground">No coverage shape set</div>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={onDelete}>
            Delete
          </Button>
        </CardContent>
      </Card>
    </li>
  );
}
